import React, { useId } from 'react';

/**
 * XiBackdrop — the cinematic environment shared by every route.
 *
 * Asset-free by design: aurora, orbital ring, star field, layered terrain and
 * film grain are all CSS/SVG. Dark and light are art-directed separately via
 * body[data-theme] in `styles/xi-ui.css`.
 */
export default function XiBackdrop({ variant = 'home' }) {
  const uid = useId().replace(/:/g, '');
  const id = (name) => `xi-${name}-${uid}`;

  return (
    <div className={`xi-backdrop xi-backdrop--${variant}`} aria-hidden="true">
      <span className="xi-backdrop__aurora" />
      <span className="xi-backdrop__ring" />
      <span className="xi-backdrop__ring xi-backdrop__ring--inner" />
      <span className="xi-backdrop__stars" />

      <svg
        className="xi-backdrop__terrain"
        viewBox="0 0 1440 460"
        preserveAspectRatio="none"
        focusable="false"
      >
        <defs>
          <linearGradient id={id('ridge-far')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--x-terrain-1)" stopOpacity="0.85" />
            <stop offset="100%" stopColor="var(--x-terrain-2)" stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id={id('ridge-mid')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--x-terrain-2)" />
            <stop offset="100%" stopColor="var(--x-terrain-3)" />
          </linearGradient>
          <linearGradient id={id('ridge-near')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--x-terrain-3)" />
            <stop offset="100%" stopColor="var(--x-bg-deep)" />
          </linearGradient>
          <radialGradient id={id('horizon')} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--x-horizon)" stopOpacity="0.55" />
            <stop offset="55%" stopColor="var(--x-aurora-1)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--x-aurora-1)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <ellipse cx="720" cy="292" rx="660" ry="86" fill={`url(#${id('horizon')})`} />

        <path
          d="M0 318 C 150 262, 286 258, 420 292 C 548 324, 636 330, 742 300 C 860 266, 942 236, 1052 262 C 1176 292, 1290 262, 1440 296 L1440 460 L0 460 Z"
          fill={`url(#${id('ridge-far')})`}
        />
        <path
          d="M0 360 C 128 318, 250 300, 372 330 C 500 362, 590 372, 700 348 C 828 320, 918 296, 1032 322 C 1160 352, 1300 330, 1440 356 L1440 460 L0 460 Z"
          fill={`url(#${id('ridge-mid')})`}
        />
        <path
          d="M0 408 C 160 372, 300 366, 440 392 C 600 422, 700 424, 840 398 C 980 372, 1120 366, 1260 390 C 1330 402, 1390 404, 1440 398 L1440 460 L0 460 Z"
          fill={`url(#${id('ridge-near')})`}
        />
        <path
          d="M0 408 C 160 372, 300 366, 440 392 C 600 422, 700 424, 840 398 C 980 372, 1120 366, 1260 390 C 1330 402, 1390 404, 1440 398"
          fill="none"
          stroke="var(--x-horizon)"
          strokeOpacity="0.35"
          strokeWidth="1.2"
        />
      </svg>

      <span className="xi-backdrop__grain" />
    </div>
  );
}
